<div class="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
  <div class="bg-white rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden">
    <!-- Header -->
    <div class="flex items-start justify-between px-6 py-5 border-b border-gray-200">
      <div>
        <h2 class="text-xl font-semibold text-gray-900">Créditos</h2>
        <p class="text-sm text-gray-600 mt-1">Compra créditos para tu cuenta.</p>
      </div>

      <div class="flex items-center gap-4">
        <!-- Toggle Créditos / Mi saldo -->
        <div class="rounded-2xl border border-brand shadow-sm overflow-hidden">
          <div class="flex bg-brand-surface-alt">
            <button
              type="button"
              class="px-4 py-2 text-sm font-semibold transition"
              [ngClass]="viewMode === 'credits' ? 'bg-white text-brand-primary shadow-sm' : 'text-brand-muted'"
              (click)="setViewMode('credits')"
            >
              Créditos
            </button>
            <!-- On-demand deshabilitado
            <button
              type="button"
              class="px-4 py-2 text-sm font-semibold transition"
              [ngClass]="viewMode === 'ondemand' ? 'bg-white text-brand-primary shadow-sm' : 'text-brand-muted'"
              (click)="setViewMode('ondemand')"
            >
              On-demand
            </button>
            -->
            <button
              type="button"
              class="px-4 py-2 text-sm font-semibold transition"
              [ngClass]="viewMode === 'balance' ? 'bg-white text-brand-primary shadow-sm' : 'text-brand-muted'"
              (click)="setViewMode('balance')"
            >
              Mi saldo
            </button>
          </div>
        </div>

        <!-- Close button -->
        <button
          type="button"
          class="text-gray-400 hover:text-gray-600 transition ml-2"
          (click)="closeModal()"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
      <div class="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
        <p class="text-sm text-gray-700">
          <span class="font-semibold">1 crédito = 1 peso colombiano (COP)</span> dentro de la plataforma.
          Los créditos sirven igual para cualquier tipo de firma; lo que cambia es cuántos créditos se consumen por documento.
        </p>
      </div>

      <div *ngIf="isLoading" class="text-sm text-gray-500 animate-pulse">
        Cargando información de precios...
      </div>

      <!-- Créditos -->
      <ng-container *ngIf="!isLoading && viewMode === 'credits'">
        <div *ngIf="!hasPurchasePricing" class="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p class="text-sm text-amber-800">
            No encontramos un precio específico de compra de créditos en el API. Mostramos la compra a
            <span class="font-semibold">1 COP por crédito</span> y mantenemos el detalle de consumo por tipo de firma.
          </p>
        </div>

        <section class="grid lg:grid-cols-2 gap-5">
          <div class="rounded-2xl border border-gray-200 bg-white p-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.25em] text-brand-primary">Compra de créditos</p>
                <h3 class="text-lg font-semibold text-gray-900 mt-2">Elige cuántos créditos quieres cargar</h3>
                <p class="text-sm text-gray-600 mt-1">Ajusta el control y mira cómo cambia el precio por volumen.</p>
              </div>
              <button
                type="button"
                class="px-3 py-2 rounded-xl border text-xs font-semibold text-gray-700 hover:bg-gray-50"
                (click)="loadMeters()"
              >
                Actualizar
              </button>
            </div>

            <div class="mt-5">
              <div class="flex items-end justify-between gap-4">
                <div>
                  <p class="text-xs text-gray-500">Créditos seleccionados</p>
                  <p class="text-3xl font-semibold text-gray-900">{{ formatCredits(creditsQty) }}</p>
                </div>
                <div class="w-48">
                  <label class="text-xs text-gray-500">Editar (COP)</label>
                  <div class="relative mt-1">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                    <input
                      type="number"
                      class="w-full rounded-xl border border-gray-300 pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                      [min]="creditsMin"
                      [max]="creditsMax"
                      [step]="creditsStep"
                      [(ngModel)]="creditsQty"
                      (ngModelChange)="onCreditsQtyChange($event)"
                    />
                  </div>
                </div>
              </div>

              <input
                type="range"
                class="mt-5 w-full accent-brand-primary-medium"
                [min]="creditsMin"
                [max]="creditsMax"
                [step]="creditsStep"
                [(ngModel)]="creditsQty"
                (ngModelChange)="onCreditsQtyChange($event)"
              />

              <div class="mt-3 flex items-center justify-between text-xs text-gray-500">
                <span>{{ formatCredits(creditsMin) }}</span>
                <span>{{ formatCredits(creditsMax) }}</span>
              </div>
            </div>
          </div>

          <div class="rounded-2xl border border-gray-200 bg-white p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.25em] text-brand-primary">Resumen</p>
            <div class="mt-3 grid grid-cols-2 gap-4">
              <div class="rounded-2xl bg-gray-50 border border-gray-100 p-4">
                <p class="text-xs text-gray-500">Precio unitario aplicado</p>
                <p class="text-lg font-semibold text-gray-900 mt-1">
                  {{ hasPurchasePricing ? formatMoney(purchaseUnitPrice, currency) : formatMoney(1, currency) }}
                </p>
                <p class="text-xs text-gray-500 mt-1">Por crédito · Escala {{ purchaseTierLabel }}</p>
              </div>
              <div class="rounded-2xl bg-gray-50 border border-gray-100 p-4">
                <p class="text-xs text-gray-500">Total a pagar</p>
                <p class="text-lg font-semibold text-gray-900 mt-1">{{ formatMoney(totalToPay, currency) }}</p>
                <p class="text-xs text-gray-500 mt-1">Recibes {{ formatCredits(creditsQty) }} créditos</p>
              </div>
            </div>

            <div *ngIf="hasPurchasePricing && savingsAmount > 0" class="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p class="text-sm text-emerald-800">
                Ahorro por volumen: <span class="font-semibold">{{ formatMoney(savingsAmount, currency) }}</span>
                <span class="text-emerald-700">({{ savingsPct }}%)</span>
              </p>
            </div>

            <div class="mt-4 text-sm text-gray-600">
              <p class="leading-relaxed">
                Los créditos se cargan a tu cuenta y se descuentan automáticamente cuando firmas documentos.
                Si tu consumo crece, el precio por documento puede bajar según el volumen (escalas).
              </p>
            </div>
          </div>
        </section>

        <section class="rounded-2xl border border-gray-200 bg-white p-5">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.25em] text-brand-primary">Consumo por tipo de firma</p>
              <h3 class="text-lg font-semibold text-gray-900 mt-2">¿Cuántos créditos consume cada documento?</h3>
              <p class="text-sm text-gray-600 mt-1">
                Con {{ formatCredits(creditsQty) }} créditos como referencia, este sería el costo por documento en cada canal.
              </p>
            </div>
            <span class="text-xs text-gray-500">Región {{ region }} · {{ currency }}</span>
          </div>

          <div *ngIf="signatureMeters.length === 0" class="mt-4 text-sm text-gray-500">
            No hay meters de firma disponibles para mostrar.
          </div>

          <div *ngIf="signatureMeters.length > 0" class="mt-4 max-h-[340px] overflow-y-auto pr-2">
            <div class="grid md:grid-cols-2 gap-4">
              <div
                *ngFor="let meter of signatureMeters"
                class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-sm font-semibold text-gray-900">{{ meter.display_name }}</p>
                    <p class="text-xs text-gray-600 mt-1">{{ meter.description }}</p>
                  </div>
                  <span
                    class="text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-wide"
                    [ngClass]="meter.active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'"
                  >
                    {{ meter.active ? 'Activo' : 'Inactivo' }}
                  </span>
                </div>

                <div class="mt-4 flex items-end justify-between">
                  <div>
                    <p class="text-xs text-gray-500">Créditos por documento</p>
                    <p class="text-2xl font-semibold text-gray-900 mt-1">
                      {{ creditsPerDoc(meter) === null ? '—' : formatCredits(creditsPerDoc(meter)!) }}
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="text-xs text-gray-500">Escala aplicada</p>
                    <p class="text-sm font-semibold text-gray-900 mt-1">{{ meterTierLabel(meter) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="grid md:grid-cols-2 gap-4">
          <div class="rounded-2xl border border-gray-200 bg-white p-5">
            <p class="text-sm font-semibold text-gray-900">¿Cuándo conviene usar créditos?</p>
            <ul class="mt-3 space-y-2 text-sm text-gray-600">
              <li class="flex items-start gap-2">
                <span class="mt-1 w-4 h-4 rounded-full border border-emerald-500 flex items-center justify-center">
                  <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                </span>
                <span>Si quieres control y presupuesto previsible.</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="mt-1 w-4 h-4 rounded-full border border-emerald-500 flex items-center justify-center">
                  <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                </span>
                <span>Si compras por volumen y buscas mejor precio unitario.</span>
              </li>
            </ul>
          </div>

          <!-- On-demand deshabilitado
          <div class="rounded-2xl border border-gray-200 bg-white p-5">
            <p class="text-sm font-semibold text-gray-900">¿Y on-demand?</p>
            <p class="text-sm text-gray-600 mt-2">
              Ideal si tu consumo es irregular: firmas libremente y se factura al cierre del periodo.
            </p>
            <button
              type="button"
              class="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:underline"
              (click)="setViewMode('ondemand')"
            >
              Ver cómo funciona on-demand
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14m-7-7 7 7-7 7" />
              </svg>
            </button>
          </div>
          -->
        </section>
      </ng-container>

      <!-- On-demand deshabilitado
      <ng-container *ngIf="!isLoading && viewMode === 'ondemand'">
        <section class="rounded-2xl border border-gray-200 bg-white p-5">
          <p class="text-xs font-semibold uppercase tracking-[0.25em] text-brand-primary">On-demand (pospago)</p>
          <h3 class="text-lg font-semibold text-gray-900 mt-2">Firma sin comprar créditos</h3>
          <p class="text-sm text-gray-600 mt-1">
            En on-demand no recargas saldo: consumes libremente y recibes la factura automáticamente en tu fecha de corte.
          </p>

          <div class="mt-4 grid md:grid-cols-2 gap-4">
            <div class="rounded-2xl bg-gray-50 border border-gray-100 p-4">
              <p class="text-sm font-semibold text-gray-900">Qué verás</p>
              <ul class="mt-3 space-y-2 text-sm text-gray-600">
                <li>Consumo acumulado del periodo.</li>
                <li>Factura al cierre, sin recargas previas.</li>
                <li>Más flexible si tu volumen varía.</li>
              </ul>
            </div>
            <div class="rounded-2xl bg-gray-50 border border-gray-100 p-4">
              <p class="text-sm font-semibold text-gray-900">Cuándo conviene</p>
              <ul class="mt-3 space-y-2 text-sm text-gray-600">
                <li>Equipos con consumo impredecible.</li>
                <li>Quieres minimizar pasos de compra.</li>
                <li>Prefieres pago a fin de mes/corte.</li>
              </ul>
            </div>
          </div>

          <button
            type="button"
            class="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-accent text-white text-sm font-semibold hover:bg-brand-accent-hover"
            routerLink="/administration/calculator"
            (click)="closeModal()"
          >
            Ir a la calculadora
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14m-7-7 7 7-7 7" />
            </svg>
          </button>
        </section>

        <section class="rounded-2xl border border-gray-200 bg-white p-5">
          <p class="text-sm font-semibold text-gray-900">¿Quieres comparar con créditos?</p>
          <p class="text-sm text-gray-600 mt-2">
            Si prefieres control de presupuesto y saldo prepagado, vuelve a Créditos para elegir un monto y ver el costo por documento.
          </p>
          <button
            type="button"
            class="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:underline"
            (click)="setViewMode('credits')"
          >
            Volver a compra de créditos
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 12H5m7 7-7-7 7-7" />
            </svg>
          </button>
        </section>
      </ng-container>
      -->

      <!-- Mi saldo -->
      <ng-container *ngIf="viewMode === 'balance'">
        <section class="rounded-2xl border border-gray-200 bg-white p-5">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.25em] text-brand-primary">Billetera</p>
              <h3 class="text-lg font-semibold text-gray-900 mt-2">Tus créditos actuales</h3>
              <p class="text-sm text-gray-600 mt-1">Consulta tu saldo disponible y créditos reservados.</p>
            </div>
            <button
              type="button"
              class="px-3 py-2 rounded-xl border text-xs font-semibold text-gray-700 hover:bg-gray-50"
              (click)="loadWallet()"
            >
              Actualizar
            </button>
          </div>

          <div *ngIf="isLoadingWallet" class="mt-4 text-sm text-gray-500 animate-pulse">Cargando saldo...</div>
          <div *ngIf="!isLoadingWallet && walletError" class="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {{ walletError }}
          </div>

          <div *ngIf="!isLoadingWallet && walletInfo" class="mt-4 grid md:grid-cols-2 gap-4">
            <div class="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
              <p class="text-xs text-emerald-700">Saldo disponible</p>
              <p class="text-3xl font-semibold text-emerald-700 mt-2">{{ walletInfo.balance | number }} créditos</p>
            </div>
            <div class="rounded-2xl bg-amber-50 border border-amber-200 p-4">
              <p class="text-xs text-amber-700">Créditos reservados</p>
              <p class="text-3xl font-semibold text-amber-700 mt-2">{{ walletInfo.reserved | number }} créditos</p>
            </div>
            <div class="md:col-span-2 text-xs text-gray-500">
              Última actualización: {{ walletInfo.updatedAt | date:'short' }}
            </div>
          </div>
        </section>
      </ng-container>
    </div>

    <!-- Footer (fixed at bottom) -->
    <div class="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="px-6 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition"
          (click)="closeModal()"
        >
          Cancelar
        </button>
        <a
          class="text-sm font-semibold text-gray-500 hover:text-gray-700 hover:underline"
          routerLink="/administration/pricing"
          (click)="closeModal()"
        >
          Ver precios
        </a>
      </div>

      <button
        *ngIf="viewMode === 'credits'"
        type="button"
        class="px-8 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        [disabled]="isProcessingPayment || !hasPurchasePricing"
        (click)="proceedToPayment()"
      >
        <svg *ngIf="isProcessingPayment" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {{ isProcessingPayment ? 'Procesando...' : 'Ir a pagar' }}
        <svg *ngIf="!isProcessingPayment" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </button>

      <button
        *ngIf="viewMode === 'balance'"
        type="button"
        class="px-8 py-2.5 rounded-lg bg-gray-600 text-white text-sm font-semibold hover:bg-gray-700 transition"
        (click)="closeModal()"
      >
        Cerrar
      </button>
    </div>
  </div>
</div>
